import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Info } from '@phosphor-icons/react';
import { OnboardingStep, type Platform } from '@reelplicate/api-client/model';
import {
  getGetPlatformsQueryKey,
  useGetPlatforms,
  useUpdatePlatforms,
} from '@reelplicate/api-client/platforms';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  BrandMark,
  type BrandMarkName,
  Button,
  ChoiceCard,
  ChoiceCardGroup,
  Stack,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { OnboardingActions, OnboardingCard, OnboardingHeader } from './onboarding-card';
import { useOnboardingProgress } from './progress';
import {
  PlatformsStepSchema,
  type PlatformsStepValues,
  SAMPLE_PLATFORMS,
} from './schemas';
import { ONBOARDING_STEPS, stepPath } from './steps';

const OPTIONS: {
  value: Platform;
  mark: BrandMarkName;
  title: string;
  description: string;
}[] = [
  {
    value: 'TIKTOK',
    mark: 'tiktok',
    title: 'TikTok',
    description: '9:16 · up to 10 min',
  },
  {
    value: 'INSTAGRAM',
    mark: 'instagram',
    title: 'Instagram Reels',
    description: '9:16 · up to 3 min',
  },
  {
    value: 'YOUTUBE',
    mark: 'youtube',
    title: 'YouTube Shorts',
    description: '9:16 · up to 3 min',
  },
  {
    value: 'LINKEDIN',
    mark: 'linkedin',
    title: 'LinkedIn',
    description: '1:1 or 4:5 · up to 10 min',
  },
];

/**
 * Figma "Onboarding — 4 · Platforms" (127:737): checkbox Choice Cards, one
 * per platform (a 24px brand mark in the 40px visual slot, top-left), 10px
 * apart, and a Caption note with an info icon. Saved to the workspace;
 * until then TikTok and Instagram start ticked.
 */
export function PlatformsStepPage() {
  const navigate = useNavigate();
  const form = useForm<PlatformsStepValues>({
    resolver: zodResolver(PlatformsStepSchema),
    defaultValues: SAMPLE_PLATFORMS,
  });
  const { advance } = useOnboardingProgress();
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id;
  const { data: saved } = useGetPlatforms(workspaceId ?? '', {
    query: { enabled: Boolean(workspaceId) },
  });
  const update = useUpdatePlatforms();

  // Back from a later step: show what was saved.
  useEffect(() => {
    if (!saved?.platforms.length || form.formState.isDirty) return;
    form.reset({ platforms: saved.platforms });
  }, [saved, form]);

  const onSubmit = form.handleSubmit(async ({ platforms }) => {
    if (!workspaceId) return;
    try {
      await update.mutateAsync({ id: workspaceId, data: { platforms } });
    } catch {
      return; // The failure is already shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getGetPlatformsQueryKey(workspaceId),
    });
    if (await advance(OnboardingStep.PLAN)) await navigate(stepPath(5));
  });

  return (
    <OnboardingCard loose onSubmit={onSubmit}>
      <OnboardingHeader
        eyebrow={`Step 4 of ${ONBOARDING_STEPS.length}`}
        title="Where do you publish?"
        subtitle="We’ll size and caption every reel for each platform. Connect accounts now or later."
      />

      <Stack className="gap-2.5">
        <Controller
          name="platforms"
          control={form.control}
          render={({ field, fieldState }) => (
            <Stack gap={2}>
              <ChoiceCardGroup
                type="checkbox"
                aria-label="Platforms"
                value={field.value}
                onValueChange={(next) => field.onChange(next)}
                className="gap-2.5"
              >
                {OPTIONS.map((option) => (
                  <ChoiceCard
                    key={option.value}
                    value={option.value}
                    title={option.title}
                    description={option.description}
                    visual={
                      <span className="size-10">
                        <BrandMark name={option.mark} size={24} />
                      </span>
                    }
                  />
                ))}
              </ChoiceCardGroup>
              {fieldState.error && (
                <p role="alert" className="text-caption text-status-danger-fg">
                  {fieldState.error.message}
                </p>
              )}
            </Stack>
          )}
        />
        <p className="text-caption text-text-secondary flex items-center gap-1.5">
          <Info aria-hidden className="text-icon-secondary size-4 shrink-0" />
          You can connect accounts from Settings at any time.
        </p>
      </Stack>

      <OnboardingActions back={stepPath(3)}>
        <Button
          type="submit"
          size="lg"
          trailingIcon={ArrowRight}
          loading={form.formState.isSubmitting}
        >
          Continue
        </Button>
      </OnboardingActions>
    </OnboardingCard>
  );
}
